import { useAuth } from "@/context/AuthContext";
import BetaOnboardingFlow from "@/components/BetaOnboardingFlow";

/**
 * Access gate: if the current user is a beta participant AND has not completed
 * the mandatory onboarding survey, render <BetaOnboardingFlow /> INSTEAD of
 * the app shell — regardless of route.
 *
 * The gate is a wrapper, not a modal — AppShell (and therefore the sidebar,
 * routing chrome, feedback widget, etc.) never mounts, so there is nothing
 * for the learner to click around. Direct URL navigation to /cards, /learners
 * etc still lands here.
 *
 * Non-beta users (platform admin, demo accounts, anyone who never redeemed a
 * beta code) skip the gate entirely.
 */
export default function BetaOnboardingGate({ children }) {
  const { user } = useAuth();
  if (!user) return children; // Protected wrapper handles unauth state
  // Platform admins skip the gate — they need `/admin` access to see the
  // Beta Feedback dashboard, and gating them would create a chicken-and-egg
  // problem if they haven't personally completed the survey.
  if (user.is_platform_admin) return children;
  // Demo accounts skip the gate (ephemeral 30-min sandboxes).
  if (user.email && user.email.startsWith("demo-")) return children;
  const isBetaParticipant = Boolean(user.beta_access_type);
  const alreadyDone = Boolean(user.beta_onboarding_completed_at);
  if (isBetaParticipant && !alreadyDone) {
    return <BetaOnboardingFlow />;
  }
  return children;
}
